import type { Action } from '@reduxjs/toolkit';

import type { LiveStreamPreferences, LiveStreamState } from '../types';

import { login, logout } from 'app/redux/actions/auth';
import type { InitAction } from 'app/redux/actions/init';
import { INIT_ACTION } from 'app/redux/actions/init';

import type {
  ApplyLiveStreamEntries,
  CloseLiveStream,
  SelectStream,
  SelectStreamWithAutoplay,
  SetDefaultStream,
  SetPipMode,
  SetStoredPreferences,
  ToggleMutedState,
} from '../actions';
import {
  APPLY_LIVE_STREAM_ENTRIES,
  CLOSE_LIVE_STREAM,
  SELECT_STREAM,
  SELECT_STREAM_WITH_AUTOPLAY,
  SET_DEFAULT_STREAM,
  SET_PIP_MODE,
  SET_STORED_PREFERENCES,
  TOGGLE_MUTED_STATE,
} from '../actions';
import entries from './entries';

// ---

const initialLiveStreamPreferences: LiveStreamPreferences = {
  closed: true,
  muted: true,
  touched: true,
  pipMode: false,
  streamId: null,
};

const initialState: LiveStreamState = {
  loading: true,
  preferences: initialLiveStreamPreferences,
  entries: entries(undefined, INIT_ACTION),
};

export type LoginSuccess = Action<typeof login.fulfilled>;
export type LogoutSuccess = Action<typeof logout.fulfilled>;

export type LiveStreamActions =
  | InitAction
  | LoginSuccess
  | LogoutSuccess
  | SelectStream
  | SelectStreamWithAutoplay
  | SetDefaultStream
  | ApplyLiveStreamEntries
  | CloseLiveStream
  | ToggleMutedState
  | SetPipMode
  | SetStoredPreferences;

// ---

function changeLiveStreamPreferences(
  state: LiveStreamState,
  value: Partial<LiveStreamPreferences>,
): LiveStreamState {
  return {
    ...state,
    preferences: {
      ...state.preferences,
      ...value,
    },
  };
}

export default function (state = initialState, action: LiveStreamActions): LiveStreamState {
  const newEntries = entries(state.entries, action as ApplyLiveStreamEntries);

  if (state.entries !== newEntries) {
    state = {
      ...state,
      entries: newEntries,
    };
  }

  switch (action.type) {
    case SELECT_STREAM:
      return changeLiveStreamPreferences(state, { streamId: action.payload.matchId });

    case SELECT_STREAM_WITH_AUTOPLAY:
      return changeLiveStreamPreferences(
        state,
        {
          closed: false,
          streamId: action.payload.matchId,
        },
      );

    case SET_DEFAULT_STREAM:
      return changeLiveStreamPreferences(state, { streamId: null });

    case CLOSE_LIVE_STREAM:
      return changeLiveStreamPreferences(state, { closed: true });

    case APPLY_LIVE_STREAM_ENTRIES:
      return {
        ...state,
        loading: false,
      };

    case SET_STORED_PREFERENCES:
      return changeLiveStreamPreferences(state, action.payload.preferences);

    case TOGGLE_MUTED_STATE:
      return changeLiveStreamPreferences(state, { muted: action.payload.muteState });

    case SET_PIP_MODE:
      return changeLiveStreamPreferences(state, { pipMode: action.payload.pipMode });

    case login.fulfilled.toString():
    case logout.fulfilled.toString():
      return changeLiveStreamPreferences(state, initialLiveStreamPreferences);

    default:
      return state;
  }
}
